import { useCallback, useEffect, useState } from 'react'

/**
 * Embla autoplay controller hook.
 *
 * Reads the autoplay plugin from emblaApi and exposes a stable API to:
 * - observe current autoplay state (`autoplayIsPlaying`)
 * - start, stop, or toggle autoplay safely
 * - run a custom callback after forcing autoplay to stop,
 *   for example: when user interacts with a video trailer
 */
const useAutoplay = (emblaApi) => {
  const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(false)

  const onAutoplayButtonClick = useCallback(
    (callback) => {
      const autoplay = emblaApi?.plugins()?.autoplay
      if (!autoplay) return

      autoplay.stop()
      callback()
    },
    [emblaApi]
  )

  const toggleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) return

    const playOrStop = autoplay.isPlaying() ? autoplay.stop : autoplay.play
    playOrStop()
  }, [emblaApi])

  const startAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay || autoplay.isPlaying()) return

    autoplay.play()
  }, [emblaApi])

  const stopAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay || !autoplay.isPlaying()) return

    autoplay.stop()
  }, [emblaApi])

  const resetAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) return

    autoplay.reset()
  }, [emblaApi])

  const restartAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) return

    if (autoplay.isPlaying()) {
      autoplay.reset()
      return
    }

    autoplay.play()
  }, [emblaApi])

  useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) return

    setAutoplayIsPlaying(autoplay.isPlaying())
    emblaApi
      .on('autoplay:play', () => setAutoplayIsPlaying(true))
      .on('autoplay:stop', () => setAutoplayIsPlaying(false))
      .on('reinit', () => setAutoplayIsPlaying(autoplay.isPlaying()))
  }, [emblaApi])

  return {
    autoplayIsPlaying,
    startAutoplay,
    stopAutoplay,
    resetAutoplay,
    restartAutoplay,
    toggleAutoplay,
    onAutoplayButtonClick
  }
}

export default useAutoplay
